import { Add, Dashboard, Delete } from "assets/icons/Common";
import { RightSideBar } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { ModalsContext } from "contexts/ModalsContext";
import { LOCALSTORAGE_WIDGET_KEY } from "hooks/widgets";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { TYPE_ACTION } from "types/dashboard";
import { DashboardProps } from "types/GlobalData";
import { findDashboardGroupById, getValidDashboards } from "util/dashboardGroups";
import { loadData, saveData, forceSaveData } from "util/wp-api";

import { MODAL_DELETE_DASHBOARD } from "./constants";
import { ModalDeleteDashboard } from "./ModalDeleteDashboard";
import { DeleteBtn, LabelInput, SaveBtn, SubTitle, Title } from "./multipleDashboards.styles";

interface MultipleDashboardsBoxProps {
  onClose: (data: DashboardProps, type?: TYPE_ACTION) => void;
  isOpen: boolean;
  textPagination: string;
  hasDeleteBtn: boolean;
  currentIndex: number;
}

export const DashboardSettings = (props: MultipleDashboardsBoxProps) => {
  const { onClose, isOpen, textPagination, hasDeleteBtn, currentIndex } = props;

  const modalsContext = useContext(ModalsContext);
  const { data: globalData, setData: updateGlobalData } = useContext(DashboardContext);
  const dashboards = useMemo(
    () =>
      getValidDashboards(
        findDashboardGroupById(globalData.dashboardGroups, globalData.activeDashboardGroupId)
          ?.dashboards,
      ),
    [globalData.dashboardGroups, globalData.activeDashboardGroupId],
  );

  const [data, setData] = useState<DashboardProps>(dashboards[currentIndex]);
  const [hasLimit, setHasLimit] = useState(false);

  useEffect(() => {
    const currentData = dashboards[currentIndex];

    setData(currentData);
  }, [currentIndex, globalData?.dashboardGroups, dashboards]);

  useEffect(() => {
    const nameLength = data?.name?.length ?? 0;
    if (nameLength === 20) {
      setHasLimit(true);
    } else if (hasLimit && nameLength < 20) {
      setHasLimit(false);
    }
  }, [data, hasLimit]);

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setData((prevState) => ({
      ...prevState,
      name: event.target.value,
    }));
    const nameLength = event?.target?.value?.length ?? 0;
    if (nameLength === 20) {
      setHasLimit(true);
    } else if (hasLimit && nameLength < 20) {
      setHasLimit(false);
    }
  };

  const handleOpenDeleteModal = () => {
    modalsContext.openModal({
      name: MODAL_DELETE_DASHBOARD,
      id: data.id,
      currentIndex,
    });
  };

  const handleClose = () => {
    onClose(data, TYPE_ACTION.update);
  };

  const handleDelete = () => {
    onClose(data, TYPE_ACTION.delete);
  };

  const handleSaveSlide = async () => {
    try {
      // Load current widget data from localStorage to get the latest state
      const currentWidgetData = await loadData(LOCALSTORAGE_WIDGET_KEY);
      const widgets: any = { ...currentWidgetData }; // Start with all existing widget data

      // Update dashboard data in global state
      const updatedDashboardGroups = globalData.dashboardGroups.map((group) => {
        if (group?.id === globalData.activeDashboardGroupId) {
          return {
            ...group,
            dashboards: group.dashboards.map((dashboard) =>
              dashboard?.id === globalData.activeDashboardId
                ? { ...dashboard, ...data }
                : dashboard,
            ),
          };
        }
        return group;
      });

      // Update global state with the new dashboard data
      updateGlobalData({
        dashboardGroups: updatedDashboardGroups,
        activeDashboardId: globalData.activeDashboardId,
        activeDashboardGroupId: globalData.activeDashboardGroupId,
      });

      // Force immediate save to ensure both dashboard and widget data are synchronized
      // This will trigger the enhanced saveData function that reads all localStorage keys
      await forceSaveData("DashboardData", {
        ...globalData,
        dashboardGroups: updatedDashboardGroups,
      });

      console.log("Slide saved successfully with synchronized localStorage state");
    } catch (error) {
      console.error("Error saving slide:", error);
    }
  };

  return (
    <>
      <RightSideBar
        isOpen={isOpen}
        onClose={handleClose}
        header={
          <>
            <Dashboard width="44px" />

            <Title>
              Slide:
              {` ${textPagination}`}
            </Title>
          </>
        }
      >
        <SubTitle>Choose slide settings</SubTitle>
        <Form.Group>
          <LabelInput $hasLimit={hasLimit}>
            Name
            <small>(Max characters: 20)</small>
          </LabelInput>
          <Form.Control
            type="text"
            maxLength={20}
            value={data ? data.name : ""}
            onChange={handleChangeName}
          />
        </Form.Group>
        <SaveBtn onClick={handleSaveSlide}>
          Save Slide
          <Add />
        </SaveBtn>
        {hasDeleteBtn ? (
          <DeleteBtn onClick={handleOpenDeleteModal}>
            Delete Slide
            <Delete />
          </DeleteBtn>
        ) : null}
      </RightSideBar>
      <ModalDeleteDashboard onDelete={handleDelete} />
    </>
  );
};
